import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Share2, Copy, Mail, Send, Users, Gift, Check, Loader2,
  Flame, X, Plus, ExternalLink, Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function ReferralPage() {
  const { user, authHeaders, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState(['']);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const FRONTEND_URL = window.location.origin;

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/referral/stats`, { headers: authHeaders() });
      setStats(res.data);
    } catch {
      toast.error('Nie udało się załadować statystyk');
    }
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const referralLink = stats ? `${FRONTEND_URL}/auth?ref=${stats.referral_code}` : '';

  const copyLink = () => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(referralLink).then(() => {
        setCopied(true);
        toast.success('Link skopiowany!');
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy());
    } else {
      fallbackCopy();
    }
  };

  const fallbackCopy = () => {
    const textArea = document.createElement('textarea');
    textArea.value = referralLink;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      toast.success('Link skopiowany!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Nie udało się skopiować. Skopiuj ręcznie.');
    }
    document.body.removeChild(textArea);
  };

  const shareWhatsApp = () => {
    const text = `Hej! Dołącz do Znajdownika - najlepsze okazje i promocje! Bonus +150 Zorków na start! ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareTwitter = () => {
    const text = `Dołącz do Znajdownika - łowca okazji! Najlepsze promocje i rabaty. Bonus +150 Zorków!`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareTelegram = () => {
    const text = `Hej! Dołącz do Znajdownika i poluj na okazje! +150 Zorków na start!`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Dołącz do Znajdownika!',
          text: `${user?.nick} zaprasza Cię do Znajdownika - łowca okazji! +150 Zorków na start!`,
          url: referralLink
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  const addEmailField = () => {
    if (emails.length < 10) setEmails([...emails, '']);
  };

  const removeEmailField = (index) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const updateEmail = (index, value) => {
    const updated = [...emails];
    updated[index] = value;
    setEmails(updated);
  };

  const handleSendInvites = async (e) => {
    e.preventDefault();
    const validEmails = emails.filter(e => e.trim() && e.includes('@'));
    if (validEmails.length === 0) {
      toast.error('Podaj przynajmniej jeden poprawny email');
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const res = await axios.post(`${API}/referral/invite`, {
        emails: validEmails,
        message: customMessage || null
      }, { headers: authHeaders() });
      setSendResult(res.data);
      if (res.data.sent > 0) {
        toast.success(`Wysłano ${res.data.sent} zaproszeń! +${res.data.zorki_earned} Zorków`);
        refreshUser();
      } else if (res.data.results?.some(r => r.status === 'saved_not_sent')) {
        toast.info('Zaproszenia zapisane. Skonfiguruj SMTP w panelu admina, aby wysyłać emaile.');
      }
      fetchStats();
      setEmails(['']);
      setCustomMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd wysyłania');
    }
    setSending(false);
  };

  if (loading) return (
    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="referral-page">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <Share2 className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
          POLECAJ I ZARABIAJ
        </h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="referral-stats">
        <div className="bg-card border border-border/40 p-4 flex flex-col items-center text-center">
          <Users className="w-6 h-6 text-primary mb-1" />
          <span className="font-heading text-2xl font-bold text-white">{stats?.referral_count || 0}</span>
          <span className="text-xs text-muted-foreground">Poleconych</span>
        </div>
        <div className="bg-card border border-border/40 p-4 flex flex-col items-center text-center">
          <Flame className="w-6 h-6 text-zorki mb-1" />
          <span className="font-heading text-2xl font-bold text-zorki">{stats?.total_zorki_earned || 0}</span>
          <span className="text-xs text-muted-foreground">Zarobionych Zorków</span>
        </div>
        <div className="bg-card border border-border/40 p-4 flex flex-col items-center text-center">
          <Mail className="w-6 h-6 text-primary mb-1" />
          <span className="font-heading text-2xl font-bold text-white">{stats?.invites?.length || 0}</span>
          <span className="text-xs text-muted-foreground">Zaproszeń</span>
        </div>
      </div>

      {/* Referral Link + Share */}
      <div className="bg-card border border-border/40 p-6 mb-6" data-testid="referral-link-section">
        <h2 className="font-heading text-lg font-bold text-white mb-3 flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary" /> Twój link polecenia
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Podziel się linkiem - za każdą osobę która dołączy dostajesz <span className="text-zorki font-bold">+50 Zorków</span>,
          a polecony <span className="text-zorki font-bold">+150 Zorków</span> na start!
        </p>

        {/* Link field */}
        <div className="flex gap-2 mb-4">
          <Input
            readOnly
            value={referralLink}
            className="bg-input border-transparent font-mono text-xs rounded-none flex-1"
            data-testid="referral-link-input"
          />
          <Button
            onClick={copyLink}
            className={`rounded-none px-4 transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-primary text-primary-foreground'}`}
            data-testid="copy-referral-link-btn"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>

        {/* Share buttons */}
        <div className="flex flex-wrap gap-2" data-testid="share-buttons">
          <Button
            onClick={nativeShare}
            className="bg-primary text-primary-foreground rounded-none font-heading uppercase tracking-wider text-xs"
            data-testid="share-native-btn"
          >
            <Share2 className="w-4 h-4 mr-1.5" /> Udostępnij
          </Button>
          <Button
            onClick={shareWhatsApp}
            className="bg-[#25D366] text-white rounded-none font-heading uppercase tracking-wider text-xs hover:bg-[#25D366]/90"
            data-testid="share-whatsapp-btn"
          >
            WhatsApp
          </Button>
          <Button
            onClick={shareFacebook}
            className="bg-[#1877F2] text-white rounded-none font-heading uppercase tracking-wider text-xs hover:bg-[#1877F2]/90"
            data-testid="share-facebook-btn"
          >
            Facebook
          </Button>
          <Button
            onClick={shareTwitter}
            className="bg-[#1DA1F2] text-white rounded-none font-heading uppercase tracking-wider text-xs hover:bg-[#1DA1F2]/90"
            data-testid="share-twitter-btn"
          >
            Twitter / X
          </Button>
          <Button
            onClick={shareTelegram}
            className="bg-[#0088cc] text-white rounded-none font-heading uppercase tracking-wider text-xs hover:bg-[#0088cc]/90"
            data-testid="share-telegram-btn"
          >
            Telegram
          </Button>
        </div>
      </div>

      {/* Email Invitations */}
      <div className="bg-card border border-border/40 p-6 mb-6" data-testid="email-invite-section">
        <h2 className="font-heading text-lg font-bold text-white mb-3 flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" /> Zaproś przez email
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Podaj adresy email znajomych - system wyśle im zaproszenie z Twoim kodem polecenia.
          <span className="text-zorki font-bold"> +5 Zorków</span> za każde wysłane zaproszenie!
        </p>

        <form onSubmit={handleSendInvites} className="space-y-3">
          {emails.map((email, index) => (
            <div key={index} className="flex gap-2">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid={`invite-email-${index}`}
                  type="email"
                  value={email}
                  onChange={(e) => updateEmail(index, e.target.value)}
                  placeholder="znajomy@email.com"
                  className="pl-10 bg-input border-transparent focus:border-primary rounded-none"
                />
              </div>
              {emails.length > 1 && (
                <Button
                  type="button"
                  onClick={() => removeEmailField(index)}
                  className="bg-secondary text-muted-foreground hover:text-destructive rounded-none px-3"
                  data-testid={`remove-email-${index}`}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}

          {emails.length < 10 && (
            <button
              type="button"
              onClick={addEmailField}
              className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
              data-testid="add-email-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Dodaj kolejny email
            </button>
          )}

          {/* Custom message */}
          <div>
            <Label className="text-sm text-muted-foreground mb-1.5 block">Własna wiadomość (opcjonalnie)</Label>
            <Textarea
              data-testid="invite-message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Hej, sprawdź tę apkę, naprawdę warto!"
              rows={2}
              className="bg-input border-transparent focus:border-primary rounded-none resize-none"
            />
          </div>

          <Button
            data-testid="send-invites-btn"
            type="submit"
            disabled={sending}
            className="w-full bg-primary text-primary-foreground font-heading font-bold uppercase tracking-wider rounded-none h-12"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wysyłanie...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Wyślij zaproszenia</>
            )}
          </Button>
        </form>

        {/* Send results */}
        {sendResult && (
          <div className="mt-4 space-y-2" data-testid="send-results">
            {sendResult.results?.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm py-1.5 border-b border-border/20 last:border-0">
                {r.status === 'sent' && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
                {r.status === 'saved_not_sent' && <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                {r.status === 'already_invited' && <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                {r.status === 'already_registered' && <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />}
                <span className="text-muted-foreground">{r.email}</span>
                <span className="text-xs ml-auto">
                  {r.status === 'sent' && <span className="text-green-500">Wysłano</span>}
                  {r.status === 'saved_not_sent' && <span className="text-yellow-500">Zapisano (brak SMTP)</span>}
                  {r.status === 'already_invited' && <span className="text-muted-foreground">Już zaproszony</span>}
                  {r.status === 'already_registered' && <span className="text-blue-400">Już zarejestrowany</span>}
                </span>
              </div>
            ))}
            {sendResult.zorki_earned > 0 && (
              <p className="text-sm text-zorki font-bold pt-2">+{sendResult.zorki_earned} Zorków za zaproszenia!</p>
            )}
          </div>
        )}
      </div>

      {/* Referred Users */}
      {stats?.referred_users?.length > 0 && (
        <div className="bg-card border border-border/40 p-6" data-testid="referred-users-list">
          <h2 className="font-heading text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Poleceni użytkownicy ({stats.referred_users.length})
          </h2>
          <div className="space-y-2">
            {stats.referred_users.map((u, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center">
                    <span className="font-heading font-bold text-primary text-sm">{u.nick?.charAt(0).toUpperCase()}</span>
                  </div>
                  <span className="text-sm text-white">{u.nick}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zorki font-bold">+50 Z</span>
                  <span className="text-xs text-muted-foreground">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('pl-PL') : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invites History */}
      {stats?.invites?.length > 0 && (
        <div className="bg-card border border-border/40 p-6 mt-6" data-testid="invites-history">
          <h2 className="font-heading text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" /> Historia zaproszeń
          </h2>
          <div className="space-y-2">
            {stats.invites.map((inv, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                <span className="text-sm text-muted-foreground">{inv.email}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 ${
                    inv.status === 'joined' ? 'bg-green-500/20 text-green-400' :
                    inv.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-secondary text-muted-foreground'
                  }`}>
                    {inv.status === 'joined' ? 'Dołączył!' : inv.status === 'pending' ? 'Oczekuje' : inv.status}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString('pl-PL') : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
